import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import ExhibitionPreorderPicker from './ExhibitionPreorderPicker';
import { serializeExhibitionPreorder } from '../exhibitionPreorderUtils';
import { exhibitionWithDefaultRsvp } from '../exhibitionsDisplay';
import {
  normalizeIndianMobile,
  sanitizePhoneInput,
  validateCheckoutEmail,
} from '../checkout/contactValidation';
import './exhibition-rsvp.css';

const INTENT_LABELS = {
  rsvp: 'RSVP only',
  preorder: 'Pre-order for pickup',
  both: 'RSVP + pre-order',
};

function defaultIntent(row) {
  if (row?.rsvp_enabled && row?.preorder_enabled) return 'both';
  if (row?.preorder_enabled) return 'preorder';
  return 'rsvp';
}

function intentOptions(row) {
  const opts = [];
  if (row?.rsvp_enabled) {
    opts.push({ value: 'rsvp', label: 'RSVP — let us know you are coming' });
  }
  if (row?.preorder_enabled) {
    opts.push({ value: 'preorder', label: 'Pre-order — reserve pieces for pickup at the show' });
  }
  if (row?.rsvp_enabled && row?.preorder_enabled) {
    opts.push({ value: 'both', label: 'Both — RSVP and note pre-orders' });
  }
  return opts;
}

export default function ExhibitionRsvpForm({ row, onClose, onSuccess }) {
  const [open, setOpen] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [partySize, setPartySize] = useState('1');
  const [intent, setIntent] = useState(() => defaultIntent(row));
  const [preorderSelections, setPreorderSelections] = useState({});
  const [preorderExtraNote, setPreorderExtraNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const intents = useMemo(() => intentOptions(row), [row]);
  const needsPreorder = intent === 'preorder' || intent === 'both';
  const isFull = Boolean(row?.rsvp_full) && intent !== 'preorder';
  const selectedPreorderItems = useMemo(
    () => Object.values(preorderSelections).filter((item) => Number(item?.qty) > 0),
    [preorderSelections],
  );

  const close = () => {
    setOpen(false);
    onClose?.();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isFull) return;
    if (needsPreorder && selectedPreorderItems.length === 0 && !preorderExtraNote.trim()) {
      setError('Pick at least one piece or add a note about what you want.');
      return;
    }
    setError('');
    const emailNorm = validateCheckoutEmail(email);
    if (!emailNorm.ok) {
      setError(emailNorm.error);
      return;
    }
    const phoneNorm = normalizeIndianMobile(phone);
    if (!phoneNorm.ok) {
      setError(phoneNorm.error);
      return;
    }
    if (phoneNorm.phone) setPhone(phoneNorm.phone);

    setSubmitting(true);
    const preorderPayload = needsPreorder
      ? serializeExhibitionPreorder({ items: selectedPreorderItems, note: preorderExtraNote })
      : '';
    try {
      const r = await fetch('/api/exhibition_rsvp.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          exhibition_id: row.id,
          name: name.trim(),
          email: emailNorm.email,
          phone: phoneNorm.phone,
          party_size: Number(partySize) || 1,
          intent,
          preorder_notes: preorderPayload,
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || !body.ok) {
        setError(body.error || 'Could not submit. Please try again.');
        return;
      }
      setDone(true);
      onSuccess?.(body);
    } catch (err) {
      setError(err.message || 'Could not submit.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const title = row?.title?.trim() || 'Exhibition';
  const spots =
    row?.rsvp_spots_remaining != null && row?.rsvp_enabled
      ? row.rsvp_spots_remaining <= 0
        ? 'Full'
        : `${row.rsvp_spots_remaining} spot${row.rsvp_spots_remaining === 1 ? '' : 's'} left`
      : null;

  const modalTitle = done
    ? 'Thank you'
    : row.rsvp_enabled && row.preorder_enabled
      ? 'RSVP or pre-order'
      : row.preorder_enabled
        ? 'Pre-order for pickup'
        : 'RSVP';

  return createPortal(
    <div
      className="exhibition-rsvp"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`exhibition-rsvp-title-${row.id}`}
    >
      <div className={`exhibition-rsvp__panel${needsPreorder ? ' exhibition-rsvp__panel--picker' : ''}`}>
        <header className="exhibition-rsvp__head">
          <div className="exhibition-rsvp__head-copy">
            {!done ? <p className="exhibition-rsvp__eyebrow">Exhibition sign-up</p> : null}
            <h2 className="exhibition-rsvp__title" id={`exhibition-rsvp-title-${row.id}`}>
              {modalTitle}
            </h2>
            {!done ? <p className="exhibition-rsvp__subtitle">{title}</p> : null}
          </div>
          <button type="button" className="exhibition-rsvp__close" aria-label="Close" onClick={close}>
            ×
          </button>
        </header>

        {done ? (
          <div className="exhibition-rsvp__body exhibition-rsvp__body--success">
            <div className="exhibition-rsvp__success-mark" aria-hidden>
              ✓
            </div>
            <p className="exhibition-rsvp__success-text">
              We have your sign-up. We will be in touch if we need any details before the show.
            </p>
            <button type="button" className="exhibition-rsvp__submit exhibition-rsvp__submit--solo" onClick={close}>
              Close
            </button>
          </div>
        ) : (
          <form className="exhibition-rsvp__form" onSubmit={submit}>
            <div className="exhibition-rsvp__body">
              {spots ? (
                <p className={`exhibition-rsvp__spots${row?.rsvp_spots_remaining <= 0 ? ' exhibition-rsvp__spots--full' : ''}`}>
                  {spots}
                </p>
              ) : null}
              {isFull ? (
                <p className="exhibition-rsvp__alert">This show has reached RSVP capacity. You can still note a pre-order if enabled.</p>
              ) : null}

              {intents.length > 1 ? (
                <fieldset className="exhibition-rsvp__intent">
                  <legend className="exhibition-rsvp__section-label">I want to</legend>
                  <div className="exhibition-rsvp__intent-options">
                    {intents.map((opt) => (
                      <label
                        key={opt.value}
                        className={`exhibition-rsvp__intent-option${intent === opt.value ? ' exhibition-rsvp__intent-option--active' : ''}`}
                      >
                        <input
                          type="radio"
                          name={`intent-${row.id}`}
                          value={opt.value}
                          checked={intent === opt.value}
                          onChange={() => setIntent(opt.value)}
                          className="exhibition-rsvp__intent-input"
                        />
                        <span className="exhibition-rsvp__intent-dot" aria-hidden />
                        <span className="exhibition-rsvp__intent-text">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              ) : null}

              <fieldset className="exhibition-rsvp__fields">
                <legend className="exhibition-rsvp__section-label">Your details</legend>
                <div className="exhibition-rsvp__field-grid">
                  <div className="exhibition-rsvp__field">
                    <label className="exhibition-rsvp__label" htmlFor={`rsvp-name-${row.id}`}>
                      Name
                    </label>
                    <input
                      id={`rsvp-name-${row.id}`}
                      className="exhibition-rsvp__input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      maxLength={120}
                      autoComplete="name"
                    />
                  </div>
                  <div className="exhibition-rsvp__field">
                    <label className="exhibition-rsvp__label" htmlFor={`rsvp-email-${row.id}`}>
                      Email
                    </label>
                    <input
                      id={`rsvp-email-${row.id}`}
                      type="email"
                      className="exhibition-rsvp__input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                    />
                  </div>
                  <div className="exhibition-rsvp__field">
                    <label className="exhibition-rsvp__label" htmlFor={`rsvp-phone-${row.id}`}>
                      Phone / WhatsApp
                    </label>
                    <input
                      id={`rsvp-phone-${row.id}`}
                      type="tel"
                      inputMode="numeric"
                      className="exhibition-rsvp__input"
                      value={phone}
                      onChange={(e) => setPhone(sanitizePhoneInput(e.target.value))}
                      required
                      autoComplete="tel"
                    />
                  </div>
                  {row?.rsvp_enabled && intent !== 'preorder' ? (
                    <div className="exhibition-rsvp__field">
                      <label className="exhibition-rsvp__label" htmlFor={`rsvp-party-${row.id}`}>
                        Guests (including you)
                      </label>
                      <select
                        id={`rsvp-party-${row.id}`}
                        className="exhibition-rsvp__input exhibition-rsvp__input--select"
                        value={partySize}
                        onChange={(e) => setPartySize(e.target.value)}
                      >
                        {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                          <option key={n} value={String(n)}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}
                </div>
              </fieldset>

              {needsPreorder ? (
                <section className="exhibition-rsvp__preorder">
                  <div className="exhibition-rsvp__preorder-head">
                    <h3 className="exhibition-rsvp__section-label">Choose pieces to hold for pickup</h3>
                    <p className="exhibition-rsvp__hint">
                      Browse by category, pick quantities — we will have them ready at the show.
                    </p>
                  </div>
                  <ExhibitionPreorderPicker
                    selections={preorderSelections}
                    onChange={setPreorderSelections}
                    extraNote={preorderExtraNote}
                    onExtraNoteChange={setPreorderExtraNote}
                  />
                </section>
              ) : null}

              {error ? <p className="exhibition-rsvp__error" role="alert">{error}</p> : null}
            </div>

            <footer className="exhibition-rsvp__actions">
              <button type="button" className="exhibition-rsvp__cancel" onClick={close}>
                Cancel
              </button>
              <button type="submit" className="exhibition-rsvp__submit" disabled={submitting || isFull}>
                {submitting ? 'Sending…' : INTENT_LABELS[intent] || 'Submit'}
              </button>
            </footer>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
}

function rsvpButtonLabel(row, compact = false) {
  if (row?.preorder_enabled && !row?.rsvp_enabled) {
    return compact ? 'Pre-order' : 'Pre-order for pickup';
  }
  if (row?.rsvp_enabled && row?.preorder_enabled) {
    return compact ? 'RSVP' : 'RSVP & pre-order';
  }
  return 'RSVP';
}

export function ExhibitionRsvpButton({ row, compact = false, className = '' }) {
  const [active, setActive] = useState(false);
  const show = exhibitionWithDefaultRsvp(row);
  if (!show?.rsvp_enabled && !show?.preorder_enabled) return null;
  if (show?.rsvp_full && !show?.preorder_enabled) return null;

  return (
    <>
      <button
        type="button"
        className={`exhibition-show-actions__btn exhibition-show-actions__btn--accent ${className}`.trim()}
        onClick={() => setActive(true)}
      >
        {rsvpButtonLabel(show, compact)}
      </button>
      {active ? <ExhibitionRsvpForm row={show} onClose={() => setActive(false)} /> : null}
    </>
  );
}

/** Back-compat alias */
export function ExhibitionRsvpTrigger(props) {
  return <ExhibitionRsvpButton {...props} />;
}
